import { useMemo } from 'react';
import {
  AppIdMetricsDetail,
  AppIdMetricsOverview,
  TableCountsResponse,
} from '../../../api/types';
import { buildPageCards, PageStatCard } from '../utils/buildPageCards';
import {
  DASHBOARD_TOP_VERSION_COUNT,
  filterChartData,
  pickBranchDiverseTopVersions,
  processBarChartDataForMetric,
  processLineChartData,
  processUserLineChartData,
  processVersionStats,
  VersionStatRow,
} from '../utils/versionChartUtils';
import { isDisplayableAppMetricVersion } from '../../../utils/appVersionDisplay';

export interface DashboardViewModel {
  pageCards: PageStatCard[];
  allAppSessionVersionStats: VersionStatRow[];
  allUserVersionStats: VersionStatRow[];
  allGameSessionVersionStats: VersionStatRow[];
  allTestSessionVersionStats: VersionStatRow[];
  allChatSessionVersionStats: VersionStatRow[];
  advertisementVersionStats: VersionStatRow[];
  advertisementVersionStatsTop: VersionStatRow[];
  userChartTopVersions: VersionStatRow[];
  appSessionVersionStats: VersionStatRow[];
  userVersionStats: VersionStatRow[];
  gameSessionVersionStats: VersionStatRow[];
  testSessionVersionStats: VersionStatRow[];
  chatSessionVersionStats: VersionStatRow[];
  allUserVersions: VersionStatRow[];
  filteredAppSessionChartData: Record<string, unknown>[];
  filteredUserChartData: Record<string, unknown>[];
  filteredGameSessionChartData: Record<string, unknown>[];
  filteredTestSessionChartData: Record<string, unknown>[];
  filteredChatSessionChartData: Record<string, unknown>[];
  filteredAdvertisementChartData: Record<string, unknown>[];
  testBarChartData: ReturnType<typeof processBarChartDataForMetric>;
  chatBarChartData: ReturnType<typeof processBarChartDataForMetric>;
}

export function useDashboardViewModel(
  tableCounts: TableCountsResponse | null,
  overviewMetrics: AppIdMetricsOverview[],
  appSessionMetrics: AppIdMetricsDetail[],
  userMetrics: AppIdMetricsDetail[],
  gameSessionMetrics: AppIdMetricsDetail[],
  testSessionMetrics: AppIdMetricsDetail[],
  chatSessionMetrics: AppIdMetricsDetail[],
  advertisementMetrics: AppIdMetricsDetail[]
): DashboardViewModel {
  const pageCards = useMemo(() => buildPageCards(tableCounts), [tableCounts]);

  const allAppSessionVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'app_session'),
    [overviewMetrics]
  );
  const allUserVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'users'),
    [overviewMetrics]
  );
  const allGameSessionVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'game_session'),
    [overviewMetrics]
  );
  const allTestSessionVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'test_session'),
    [overviewMetrics]
  );
  const allChatSessionVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'chat_session'),
    [overviewMetrics]
  );
  const advertisementVersionStats = useMemo(
    () => processVersionStats(overviewMetrics, 'advertisements'),
    [overviewMetrics]
  );
  const advertisementVersionStatsTop = useMemo(
    () => pickBranchDiverseTopVersions(advertisementVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [advertisementVersionStats]
  );

  const appSessionVersionStats = useMemo(
    () => pickBranchDiverseTopVersions(allAppSessionVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [allAppSessionVersionStats]
  );
  const userVersionStats = useMemo(
    () => pickBranchDiverseTopVersions(allUserVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [allUserVersionStats]
  );
  const gameSessionVersionStats = useMemo(
    () => pickBranchDiverseTopVersions(allGameSessionVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [allGameSessionVersionStats]
  );
  const testSessionVersionStats = useMemo(
    () => pickBranchDiverseTopVersions(allTestSessionVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [allTestSessionVersionStats]
  );
  const chatSessionVersionStats = useMemo(
    () => pickBranchDiverseTopVersions(allChatSessionVersionStats, DASHBOARD_TOP_VERSION_COUNT),
    [allChatSessionVersionStats]
  );

  const allUserVersions = useMemo(() => {
    return Array.from(new Set(userMetrics.map(item => item.app_id)))
      .filter((version) => isDisplayableAppMetricVersion(version))
      .map(version => {
        const stat = userVersionStats.find(s => s.version === version);
        return stat || { version, today: 0, yesterday: 0, lastWeek: 0, total: 0 };
      })
      .sort((a, b) => b.total - a.total);
  }, [userMetrics, userVersionStats]);

  const userChartTopVersions = useMemo(
    () => pickBranchDiverseTopVersions(allUserVersions, DASHBOARD_TOP_VERSION_COUNT),
    [allUserVersions]
  );

  const appSessionChartData = useMemo(
    () =>
      processLineChartData(
        appSessionMetrics,
        appSessionVersionStats.map(s => s.version)
      ),
    [appSessionMetrics, appSessionVersionStats]
  );
  const userChartData = useMemo(
    () => processUserLineChartData(userMetrics, userChartTopVersions),
    [userMetrics, userChartTopVersions]
  );
  const gameSessionChartData = useMemo(
    () =>
      processLineChartData(
        gameSessionMetrics,
        gameSessionVersionStats.map(s => s.version)
      ),
    [gameSessionMetrics, gameSessionVersionStats]
  );
  const testSessionChartData = useMemo(
    () =>
      processLineChartData(
        testSessionMetrics,
        testSessionVersionStats.map(s => s.version)
      ),
    [testSessionMetrics, testSessionVersionStats]
  );
  const chatSessionChartData = useMemo(
    () =>
      processLineChartData(
        chatSessionMetrics,
        chatSessionVersionStats.map(s => s.version)
      ),
    [chatSessionMetrics, chatSessionVersionStats]
  );
  const advertisementChartData = useMemo(
    () =>
      processLineChartData(
        advertisementMetrics,
        advertisementVersionStatsTop.map(s => s.version)
      ),
    [advertisementMetrics, advertisementVersionStatsTop]
  );

  const testBarChartData = useMemo(
    () => processBarChartDataForMetric(overviewMetrics, 'test_session'),
    [overviewMetrics]
  );
  const chatBarChartData = useMemo(
    () => processBarChartDataForMetric(overviewMetrics, 'chat_session'),
    [overviewMetrics]
  );

  const filteredAppSessionChartData = useMemo(
    () => filterChartData(appSessionChartData, appSessionVersionStats.map(s => s.version)),
    [appSessionChartData, appSessionVersionStats]
  );
  const filteredUserChartData = useMemo(
    () =>
      filterChartData(userChartData, userChartTopVersions.map(s => s.version), true),
    [userChartData, userChartTopVersions]
  );
  const filteredGameSessionChartData = useMemo(
    () => filterChartData(gameSessionChartData, gameSessionVersionStats.map(s => s.version)),
    [gameSessionChartData, gameSessionVersionStats]
  );
  const filteredTestSessionChartData = useMemo(
    () => filterChartData(testSessionChartData, testSessionVersionStats.map(s => s.version)),
    [testSessionChartData, testSessionVersionStats]
  );
  const filteredChatSessionChartData = useMemo(
    () => filterChartData(chatSessionChartData, chatSessionVersionStats.map(s => s.version)),
    [chatSessionChartData, chatSessionVersionStats]
  );
  const filteredAdvertisementChartData = useMemo(
    () =>
      filterChartData(
        advertisementChartData,
        advertisementVersionStatsTop.map(s => s.version)
      ),
    [advertisementChartData, advertisementVersionStatsTop]
  );

  return {
    pageCards,
    allAppSessionVersionStats,
    allUserVersionStats,
    allGameSessionVersionStats,
    allTestSessionVersionStats,
    allChatSessionVersionStats,
    advertisementVersionStats,
    advertisementVersionStatsTop,
    appSessionVersionStats,
    userVersionStats,
    gameSessionVersionStats,
    testSessionVersionStats,
    chatSessionVersionStats,
    allUserVersions,
    userChartTopVersions,
    filteredAppSessionChartData,
    filteredUserChartData,
    filteredGameSessionChartData,
    filteredTestSessionChartData,
    filteredChatSessionChartData,
    filteredAdvertisementChartData,
    testBarChartData,
    chatBarChartData,
  };
}
